import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import ConsumerAddStudentModal from 'components/add-student-modal/consumer-add-student-modal'
import { CREATE_STUDENT } from 'components/add-student-modal/mutation'
import { gtmEvent } from 'lib/gtm'
import { getInitials } from 'lib/text-display'
import { FormShell } from './form-parts'
import SegmentedControl from './segmented-control'
import StudentPicker from './student-picker'
import useStyles from './styles'
import { findSelfStudentId, withStudentId } from './utils'

export const WHO_OPTIONS = [
  { value: 'me', label: 'Me' },
  { value: 'someone_else', label: 'Someone else' },
]

const GENERIC_ERROR = 'Sorry, an error occurred. Please try again'

// Signed-in consumer. Same data flow as the legacy `ConsumerView` and
// `TileButtons`: "Me" books the golfer's own student record (creating it with
// CREATE_STUDENT when none exists), "Someone else" picks another student or
// opens the add-student modal, and every path ends by pushing
// `&studentId=...` onto the current URL.
const ConsumerStudentForm = ({
  students,
  currentUrl,
  user,
  eventObject,
  submitLabel,
  priceLabel,
  belowCard,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()
  const [createStudent] = useMutation(CREATE_STUDENT)
  const [mode, setMode] = useState('me')
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [openAddStudent, setOpenAddStudent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const mounted = useRef(true)

  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )

  const allStudents = students || []
  const selfStudentId = findSelfStudentId(allStudents, user)
  const studentsWithoutSelf = selfStudentId
    ? allStudents.filter(student => student.id !== selfStudentId)
    : allStudents

  const handleModeChange = nextMode => {
    setMode(nextMode)
    setSelectedStudentId(null)
    setError(null)
    // Legacy analytics event, fired on every tile click.
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'selected-student',
    })
  }

  const goToStudent = studentId => {
    history.push(withStudentId(currentUrl, studentId, { stripExisting: true }))
  }

  const bookForSelf = async () => {
    if (selfStudentId) {
      goToStudent(selfStudentId)
      return
    }
    setSubmitting(true)
    try {
      const { data } = await createStudent({
        variables: {
          params: {
            isMinor: false,
            studentFirstName: user?.firstName,
            studentLastName: user?.lastName,
          },
        },
      })
      if (data?.createStudent?.success) {
        goToStudent(data.createStudent.student.id)
        return
      }
      if (mounted.current) setError(GENERIC_ERROR)
    } catch (e) {
      enqueueSnackbar(GENERIC_ERROR, { variant: 'error' })
      if (mounted.current) setError(GENERIC_ERROR)
    } finally {
      if (mounted.current) setSubmitting(false)
    }
  }

  const handleSubmit = event => {
    event.preventDefault()
    setError(null)
    if (mode === 'someone_else' && !selectedStudentId) {
      setError('Select a student to continue.')
      return
    }
    gtmEvent({ ...eventObject, event: 'select-student-to-schedule' })
    if (mode === 'me') {
      bookForSelf()
    } else {
      goToStudent(selectedStudentId)
    }
  }

  return (
    <>
      <FormShell
        onSubmit={handleSubmit}
        subtitle="Who will be attending this session?"
        submitLabel={submitLabel}
        priceLabel={priceLabel}
        submitting={submitting}
        error={error}
        belowCard={belowCard}
      >
        <SegmentedControl
          name="bookingFor"
          label="Who is taking the lesson"
          options={WHO_OPTIONS}
          value={mode}
          onChange={handleModeChange}
        />
        {mode === 'me' ? (
          <div className={classes.signedIn}>
            <Avatar className={classes.pickerAvatar} aria-hidden="true">
              {getInitials(user?.name)}
            </Avatar>
            <div>
              <p className={classes.signedInName}>{user?.name}</p>
              {user?.email && (
                <p className={classes.signedInEmail}>{user.email}</p>
              )}
            </div>
          </div>
        ) : (
          <>
            {studentsWithoutSelf.length > 0 && (
              <StudentPicker
                name="someoneElseStudent"
                label="Select a student"
                students={studentsWithoutSelf}
                selectedId={selectedStudentId}
                onSelect={id => {
                  setSelectedStudentId(id)
                  setError(null)
                }}
              />
            )}
            {studentsWithoutSelf.length === 0 && (
              <p className={`${classes.emptyNote} ${classes.sectionGap}`}>
                You haven&apos;t added anyone else yet.
              </p>
            )}
            <Button
              type="button"
              variant="outlined"
              color="primary"
              className={clsx(classes.outlinedButton, classes.addStudentButton)}
              onClick={() => setOpenAddStudent(open => !open)}
            >
              Add a new student
            </Button>
          </>
        )}
      </FormShell>
      <ConsumerAddStudentModal
        open={openAddStudent}
        setOpen={setOpenAddStudent}
        onAdd={data => {
          const newStudentId = data?.createStudent?.student?.id
          history.push(`${currentUrl}&studentId=${newStudentId}`)
          gtmEvent({
            ...eventObject,
            event: 'add-add-student-to-schedule',
          })
        }}
        dialogTitle="Add a New Student"
        btnTitle="Add Student"
      />
    </>
  )
}

ConsumerStudentForm.propTypes = {
  students: PropTypes.array,
  currentUrl: PropTypes.string.isRequired,
  user: PropTypes.object,
  eventObject: PropTypes.object,
  submitLabel: PropTypes.string.isRequired,
  priceLabel: PropTypes.string,
  belowCard: PropTypes.node,
}

ConsumerStudentForm.defaultProps = {
  eventObject: {},
}

export default ConsumerStudentForm
